import React, {Component} from 'react';

import {autobind} from 'core-decorators';
import {AnimatePresence, AnimateSharedLayout, motion} from 'framer-motion';
import {withRouter} from 'next/router';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import QuickLead from '../modules/QuickLead';
import InterviewStore from 'Frontend/stores/InterviewStore';

import InfoInput from 'Frontend/components/quickInterview/InfoInput';
import SearchDecoration from 'Frontend/components/quickInterview/SearchDecoration';
import {validateQuickInterview} from 'Frontend/configs/InterviewQuestions';

import {LocationSearch} from 'Frontend/assets/images/icons';
import {FONTS} from 'Frontend/utils/fonts';
import {clampCss, iconAsBackground, pxToVw} from 'Frontend/utils/helpers';
import {MEDIA} from 'Frontend/utils/mediaQueries';
import {PRODUCT_CATEGORY} from 'Server/utils/constants';

const AnimationProps = {
    animate: {opacity: 1},
    exit: {opacity: 0},
    initial: {opacity: 0}
};

/**
 * QuickInterview
 *
 * @property {*} prop Prop comment.
 *
 * @class QuickInterview
 * @extends {Component}
 */
class QuickInterview extends Component {
    static propTypes = {
        className: PropTypes.string,
        defaultTracking: PropTypes.object,
        router: PropTypes.object
    }

    static defaultProps = {
        className: '',
        defaultTracking: {},
        router: null
    }

    /**
     * Creates an instance of QuickInterview.
     *
     * @param {Object} props Component props.
     * @memberof QuickInterview
     */
    constructor(props) {
        super(props);

        this.state = {
            data: {
                email: '',
                firstname: '',
                lastname: '',
                optin: false,
                phone: '',
                product: null,
                searchRangeCenter: null,
                searchRangeKm: null
            },
            errors: {},
            state: ''
        };
    }

    /**
     * Runs pageview Tracking.
     *
     * @memberof QuickInterview
     */
    componentDidMount() {
        const {defaultTracking, router} = this.props;
        const {isReady} = router;

        window.dataLayer.push({
            event: 'PageView',
            pageCategory: 'LeadSurvey',
            ...defaultTracking
        });

        if (isReady) {
            this.updateState();
        }
    }

    /**
     * Props did change.
     * @param {Object} prevProps Previous props.
     * @memberof QuickInterview
     */
    componentDidUpdate(prevProps) {
        const wasReady = prevProps.router.isReady;
        const {router} = this.props;
        const {isReady} = router;

        if (!wasReady && isReady) {
            this.updateState();
        }
    }

    /**
     * Update state
     *
     * @memberof QuickInterview
     */
    updateState() {
        const {router} = this.props;
        const {query} = router;
        const {place, product, range} = query;

        if (place && product && range) {
            this.setState(oldState => ({
                data: {
                    ...oldState.data,
                    product,
                    searchRangeCenter: {geo: place},
                    searchRangeKm: parseInt(range, 10)
                },
                state: 'searching'
            }));
        } else {
            this.setState({state: 'firstStep'});
        }
    }

    /**
     * Submit lead data to backend
     *
     * @memberof QuickInterview
     */
    @autobind
    async handleSubmit() {
        const {router} = this.props;
        const {data, isSubmitting} = this.state;

        if (isSubmitting) {
            return;
        }

        const errors = validateQuickInterview(data);

        if (Object.keys(errors).length > 0) {
            this.setState({errors});

            return;
        }

        this.setState({
            errors,
            isSubmitting: true
        });

        const {
            email,
            firstname,
            lastname,
            optin,
            phone,
            product,
            searchRangeCenter,
            searchRangeKm
        } = data;

        InterviewStore.answers = [
            {isQuickLead: true},
            {firstname},
            {lastname},
            {optin},
            {phone},
            {email},
            {searchRangeCenter},
            {searchRangeKm}
        ];

        // check if "product" is a real product or a "service"
        const productKeys = Object.keys(PRODUCT_CATEGORY);

        if (productKeys.includes(product)) {
            InterviewStore.answers.push({product});
        } else {
            InterviewStore.answers.push({product: 'other'});
            InterviewStore.answers.push({productService: product});
        }

        const leadId = await InterviewStore.sendForm();

        if (leadId) {
            router.replace(`/interview-short/verify/${leadId}`);
        } else {
            this.setState({isSubmitting: false});
        }
    }

    /**
     * Update form data
     *
     * @param {String} key Key
     * @param {Any} value Value
     *
     * @memberof QuickInterview
     */
    @autobind
    handleDataChange(key, value) {
        this.setState(state => ({
            data: {
                ...state.data,
                [key]: value
            },
            errors: {
                ...state.errors,
                [key]: null
            }
        }));
    }

    /**
     * On Search did complete
     * @memberof QuickInterview
     */
    @autobind
    handleSearchComplete() {
        this.setState({state: 'input'});
    }

    /**
     * Renders the Component.
     *
     * @returns {JSX} Component.
     * @memberof QuickInterview
     */
    render() {
        const {data, errors, state} = this.state;
        const {className} = this.props;

        return (
            <QuickInterviewElement className={className}>
                <LocationIcon />
                <Headline>
                    Zweithaar-Experten<br />
                    <Bold>in deiner Nähe</Bold>
                </Headline>
                <AnimateSharedLayout>
                    <AnimatePresence exitBeforeEnter>
                        {state === 'firstStep' && (
                            // eslint-disable-next-line react/jsx-props-no-spreading
                            <FirstStepContainer key="firstStep" {...AnimationProps}>
                                <StyledQuickLead showSearchIcon={false} />
                            </FirstStepContainer>
                        )}
                        {state === 'searching' && (
                            // eslint-disable-next-line react/jsx-props-no-spreading
                            <LoadingContainer key="search" {...AnimationProps}>
                                <SearchDecoration onSearchComplete={this.handleSearchComplete} />
                            </LoadingContainer>
                        )}
                        {state === 'input' && (
                            // eslint-disable-next-line react/jsx-props-no-spreading
                            <ContentContainer key="input" {...AnimationProps}>
                                <InfoInput
                                    data={data}
                                    errors={errors}
                                    onHandleDataChange={this.handleDataChange}
                                    onHandleSubmit={this.handleSubmit}
                                />
                            </ContentContainer>
                        )}
                    </AnimatePresence>
                </AnimateSharedLayout>
            </QuickInterviewElement>
        );
    }
}

export default withRouter(QuickInterview);

const FirstStepContainer = styled(motion.div)`
    width: 100%;

    ${MEDIA.TABLET.UP} {
        max-width: 860px;
    }

    ${MEDIA.DESKTOP.UP} {
        max-width: 680px;
    }
`;

const StyledQuickLead = styled(QuickLead)`
    margin-bottom: ${clampCss(20, 'm')};
    margin-top: 50px;
    width: 100%;
    z-index: 1;

    ${MEDIA.TABLET.UP} {
        margin-bottom: ${clampCss(40, 't')};
    }

    ${MEDIA.DESKTOP.UP} {
        margin-bottom: max(60px, ${pxToVw(60, 'd')});
    }
`;

const ContentContainer = styled(motion.div)`
    display: flex;
    flex: 1;
    flex-direction: row;
    position: relative;
    width: 100%;
`;

const LoadingContainer = styled(motion.div)`
    display: flex;
    flex: 1;
    padding-bottom: ${clampCss(80, 'm')};
    position: relative;
    width: 100%;

    ${MEDIA.TABLET.UP} {
        padding-bottom: ${clampCss(80, 't')};
    }

    ${MEDIA.DESKTOP.UP} {
        padding-bottom: max(180px, ${pxToVw(180, 'd')});
    }
`;

const QuickInterviewElement = styled.div`
    align-items: center;
    display: flex;
    flex: 1;
    flex-direction: column;
    height: 100%;
    min-height: 100vh;
`;

const LocationIcon = styled.div`
    background-image: url(${iconAsBackground(LocationSearch)});
    background-position: center center;
    background-repeat: no-repeat;
    background-size: contain;
    height: 85px;
    margin-bottom: 52px;
    margin-top: 85px;
    width: 70px;
`;

const Bold = styled.strong`
    ${FONTS.SEMIBOLD}
`;

const Headline = styled.h1`
    ${FONTS.LIGHT}
    color: ${({theme}) => theme.colors.textColor};
    font-size: ${clampCss(28, 'm')};
    letter-spacing: ${clampCss(1.02, 'm')};
    line-height: ${clampCss(33.6, 'm')};
    margin: 0;
    text-align: center;
    text-transform: uppercase;

    ${MEDIA.TABLET.UP} {
        font-size: ${clampCss(42, 't')};
        letter-spacing: ${clampCss(1.02, 't')};
        line-height: ${clampCss(50.4, 't')};
    }

    ${MEDIA.DESKTOP.UP} {
        font-size: max(56px, ${pxToVw(56, 'd')});
        letter-spacing: max(1.46px, ${pxToVw(1.46, 'd')});
        line-height: max(67.2px, ${pxToVw(67.2, 'd')});
    }
`;